import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { RevenueReportAggregateEnum } from 'anchor-scout-common/types';
import { Button, Page, PageContentHeader, Spinner, TableList, useStateFromUrl } from 'seagull';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { calculateStartDate, calculateEndDate } from '~/components/TableFilters/DateRangeWithPresets/DateRangeWithPresets.utils';
import TableFilters from '~/components/TableFilters/TableFilters';
import { DateRange } from '~/components/TableFilters/types';
import { routes } from '~/constants';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { formatLongMonthUSDate, formatNumericUSDate } from '~/utils';
import styles from './RevenueBreakdown.module.scss';
import { defaultAggregate, filterLabels, getDefaultSort, getHeaderItems, makeCsv, makeFiltersConfig, revenueReportColumnDefs, revenueReportHeaderColumnDefs } from './RevenueBreakdown.utils';
import { RevenueBreakdownRow } from './RevenueBreakdownRow';
import { Breakdown, FilterType } from './types';
const testIdPrefix = 'revenue-breakdown-report';
export function RevenueBreakdownReport(param) {
    let { now } = param;
    const { t: reportsTexts } = useLocaleKeys('pages.reports');
    const { t: revenueTexts } = useLocaleKeys('pages.reports.revenueBreakdown');
    const { t: commonTexts } = useLocaleKeys('common');
    const title = reportsTexts.revenueBreakdownTitle();
    const history = useHistory();
    const [revenueBreakdownReportData, setRevenueBreakdownReportData] = useState([]);
    const { urlState: { dateRange, dateRangeOption }, changeUrlState } = useStateFromUrl({
        dateRange: {
            defaultValue: [
                calculateStartDate(formatNumericUSDate(now), DateRange.Last12Months, now),
                calculateEndDate(formatNumericUSDate(now), DateRange.Last12Months, now)
            ],
            multi: true
        },
        dateRangeOption: {
            defaultValue: DateRange.Last12Months
        }
    });
    const [selectedFilters, setSelectedFilters] = useState({
        [FilterType.DateRange]: {
            dateRange,
            dateRangeOption
        },
        [FilterType.Aggregate]: defaultAggregate,
        [FilterType.Breakdowns]: []
    });
    const [isLoading, setIsLoading] = useState(false);
    const { browser: { downloadCsv } } = useIoContext();
    const breakdowns = useMemo(()=>selectedFilters[FilterType.Breakdowns] || [], [
        selectedFilters
    ]);
    const aggregate = useMemo(()=>selectedFilters[FilterType.Aggregate] || defaultAggregate, [
        selectedFilters
    ]);
    const headerItems = useMemo(()=>getHeaderItems(breakdowns, aggregate, revenueTexts), [
        aggregate,
        breakdowns
    ]);
    const sortIds = compact(headerItems.map((item)=>item.sortId));
    const defaultSort = useMemo(()=>getDefaultSort(sortIds), [
        sortIds
    ]);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const { api } = useStoreActions();
    const businessName = useStore(selectors.user.businessName);
    const handleFetchFilterConfig = async ()=>{
        const [services, customers] = await Promise.all([
            api.reports.revenueReportServicesV3(),
            api.reports.revenueReportCustomersV3()
        ]);
        const filterOptions = {
            [FilterType.ServiceName]: services,
            [FilterType.CustomerName]: customers
        };
        return makeFiltersConfig(filterOptions, revenueTexts);
    };
    const showPeriod = aggregate !== RevenueReportAggregateEnum.AllTime;
    const showCustomer = breakdowns.includes(Breakdown.Customer);
    const showService = breakdowns.includes(Breakdown.Service);
    const selectedDateRange = selectedFilters[FilterType.DateRange];
    useEffect(()=>{
        selectedDateRange && changeUrlState(selectedDateRange);
    }, [
        selectedDateRange
    ]);
    const fetchRevenueBreakdownReport = (selectedFilters, sort)=>{
        var _selectedFilters_FilterType_DateRange, _selectedFilters_FilterType_DateRange1;
        setIsLoading(true);
        api.reports.revenueReportV3({
            start: (_selectedFilters_FilterType_DateRange = selectedFilters[FilterType.DateRange]) === null || _selectedFilters_FilterType_DateRange === void 0 ? void 0 : _selectedFilters_FilterType_DateRange.dateRange[0],
            end: (_selectedFilters_FilterType_DateRange1 = selectedFilters[FilterType.DateRange]) === null || _selectedFilters_FilterType_DateRange1 === void 0 ? void 0 : _selectedFilters_FilterType_DateRange1.dateRange[1],
            breakdowns: selectedFilters[FilterType.Breakdowns] || [],
            aggregate: selectedFilters[FilterType.Aggregate],
            customers: selectedFilters[FilterType.CustomerName] || [],
            services: selectedFilters[FilterType.ServiceName] || [],
            sort: sort ? [
                sortIds[sort.colIndex],
                sort.sort
            ] : undefined
        }).then((data)=>setRevenueBreakdownReportData(data)).finally(()=>setIsLoading(false));
    };
    useSetDocumentTitle(title);
    useOnMount(()=>{
        fetchRevenueBreakdownReport(selectedFilters, sort);
    });
    const onExport = ()=>downloadCsv(makeCsv({
            headerRow: revenueReportHeaderColumnDefs[breakdowns.join(',')](aggregate, revenueTexts).map((param)=>{
                let { value } = param;
                return value;
            }),
            entries: revenueBreakdownReportData,
            showPeriod,
            showCustomer,
            showService
        }), "".concat(businessName, "_revenue_report_").concat(formatLongMonthUSDate(now), ".csv"));
    const onSortChange = (sort)=>{
        changeSort(sort);
        fetchRevenueBreakdownReport(selectedFilters, sort);
    };
    return /*#__PURE__*/ _jsx(Page, {
        testId: testIdPrefix,
        header: title,
        onClickBack: ()=>history.push(routes.reports),
        actions: /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'export_revenue_breakdown_report'
            },
            skin: "primary",
            testId: "".concat(testIdPrefix, "-export-btn"),
            onClick: onExport,
            children: commonTexts.buttons.export()
        }),
        children: /*#__PURE__*/ _jsxs(TableList.Provider, {
            columnDefs: revenueReportColumnDefs[breakdowns.join(',')](aggregate),
            numRows: revenueBreakdownReportData.length,
            sort: sort,
            onSortChange: onSortChange,
            children: [
                /*#__PURE__*/ _jsx(PageContentHeader, {
                    children: /*#__PURE__*/ _jsx("div", {
                        className: styles.pageContent,
                        children: /*#__PURE__*/ _jsx(RestfulWrapper, {
                            fetch: handleFetchFilterConfig,
                            renderLoader: ()=>/*#__PURE__*/ _jsx(Spinner, {
                                    size: 24
                                }),
                            renderChild: (param)=>{
                                let { data: filterConfig } = param;
                                return /*#__PURE__*/ _jsx(TableFilters, {
                                    testIdPrefix: testIdPrefix,
                                    filterLabels: filterLabels(revenueTexts),
                                    filterConfig: filterConfig,
                                    selectedFilters: selectedFilters,
                                    setSelectedFilters: setSelectedFilters,
                                    onRefresh: fetchRevenueBreakdownReport
                                });
                            }
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(TableList.Structure, {
                    header: /*#__PURE__*/ _jsx(TableList.Header, {
                        stickyToTop: true,
                        items: headerItems,
                        testId: "".concat(testIdPrefix, "-table-header")
                    }),
                    content: isLoading ? /*#__PURE__*/ _jsx(Spinner, {
                        centeredInBox: true
                    }) : /*#__PURE__*/ _jsx(_Fragment, {
                        children: revenueBreakdownReportData.map((reportRow, index)=>/*#__PURE__*/ _jsx(RevenueBreakdownRow, {
                                index: index,
                                day: reportRow.formattedDay,
                                customer: reportRow.customer,
                                service: reportRow.service,
                                revenue: reportRow.revenue,
                                breakdowns: breakdowns,
                                showPeriod: showPeriod,
                                showCustomer: showCustomer,
                                showService: showService
                            }, "".concat(reportRow.formattedDay, "-").concat(reportRow.customer, "-").concat(reportRow.service)))
                    })
                })
            ]
        })
    });
}
